import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { pct } from '../format';
import type { FixturePrediction, LineupSlot, OptimisedFixture, RatedPlayer, Team } from '../types';

interface Props {
  leagueId: number;
  homeTeam: Team | null;
  awayTeam: Team | null;
  homePlayers: RatedPlayer[];
  awayPlayers: RatedPlayer[];
}

const sameLineup = (a: LineupSlot[], b: LineupSlot[]): boolean =>
  a.length === b.length &&
  a.every((slot, i) => slot.kind === b[i]?.kind && slot.playerIds.join(',') === b[i]?.playerIds.join(','));

/**
 * Team win chance for a whole fixture. Lineups start from the suggestion and can
 * be edited slot by slot; every edit re-runs the prediction.
 */
export function FixturePanel({ leagueId, homeTeam, awayTeam, homePlayers, awayPlayers }: Props) {
  const [home, setHome] = useState<LineupSlot[]>([]);
  const [away, setAway] = useState<LineupSlot[]>([]);
  const [prediction, setPrediction] = useState<FixturePrediction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [optimising, setOptimising] = useState(false);
  const [optimised, setOptimised] = useState<OptimisedFixture | null>(null);
  const [maxSwitchouts, setMaxSwitchouts] = useState(2);

  const loadSuggestions = useCallback(async () => {
    if (!homeTeam || !awayTeam) {
      setHome([]);
      setAway([]);
      setPrediction(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [h, a] = await Promise.all([
        api.suggestedLineup(leagueId, homeTeam.id),
        api.suggestedLineup(leagueId, awayTeam.id),
      ]);
      setHome(h);
      setAway(a);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setHome([]);
      setAway([]);
    } finally {
      setLoading(false);
    }
  }, [leagueId, homeTeam, awayTeam]);

  useEffect(() => {
    void loadSuggestions();
  }, [loadSuggestions]);

  useEffect(() => {
    if (home.length === 0 || away.length === 0) {
      setPrediction(null);
      return;
    }
    let cancelled = false;
    api
      .predict(leagueId, home, away)
      .then((result) => {
        if (!cancelled) {
          setPrediction(result);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId, home, away]);

  /**
   * Asks the backend for the allocation with the best win chance against the
   * away lineup as it currently stands, and applies it to the home side.
   */
  const recommendLineup = async () => {
    if (!homeTeam || away.length === 0) return;
    setOptimising(true);
    setError(null);
    try {
      const result = await api.optimiseLineup({
        leagueId,
        teamId: homeTeam.id,
        opponentLineup: away,
        maxSwitchouts,
      });
      setOptimised(result);
      setHome(result.slots.map((s) => ({ kind: s.kind, playerIds: s.home.playerIds })));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setOptimising(false);
    }
  };

  const setSlotPlayer = (side: 'home' | 'away', slotIndex: number, position: number, playerId: number) => {
    const update = (slots: LineupSlot[]): LineupSlot[] =>
      slots.map((slot, i) => {
        if (i !== slotIndex) return slot;
        const playerIds = [...slot.playerIds];
        playerIds[position] = playerId;
        return { ...slot, playerIds };
      });
    if (side === 'home') setHome(update);
    else setAway(update);
  };

  if (!homeTeam || !awayTeam) {
    return (
      <section className="card">
        <header>
          <h2>Team win chance</h2>
        </header>
        <div className="empty-state">Pick a team on both sides to predict the fixture.</div>
      </section>
    );
  }

  const team = prediction?.team;

  return (
    <section className="card">
      <header>
        <h2>Team win chance</h2>
        <p className="hint">
          {prediction
            ? `${prediction.structure.filter((k) => k === 'EZ').length} singles and ` +
              `${prediction.structure.filter((k) => k === 'DP').length} doubles per fixture`
            : 'Loading the fixture layout…'}
        </p>
      </header>

      {error && <div className="notice error">{error}</div>}
      {loading && <p className="hint">Loading suggested lineups…</p>}

      {team && (
        <>
          <div className="headline-odds">
            <div className="odds-tile home">
              <div className="value">{pct(team.homeWinProbability, 0)}</div>
              <div className="label" title={homeTeam.name}>
                {homeTeam.name}
              </div>
            </div>
            <div className="odds-tile">
              <div className="value">{pct(team.drawProbability, 0)}</div>
              <div className="label">Draw ({team.gameCount / 2}:{team.gameCount / 2})</div>
            </div>
            <div className="odds-tile away">
              <div className="value">{pct(team.awayWinProbability, 0)}</div>
              <div className="label" title={awayTeam.name}>
                {awayTeam.name}
              </div>
            </div>
          </div>

          <div className="probbar" role="img" aria-label="Win, draw and loss probability">
            <span className="home" style={{ width: `${team.homeWinProbability * 100}%` }} />
            <span className="draw" style={{ width: `${team.drawProbability * 100}%` }} />
            <span className="away" style={{ width: `${team.awayWinProbability * 100}%` }} />
          </div>

          <p className="hint" style={{ marginTop: 10 }}>
            Expected points <strong>{team.expectedPointsHome.toFixed(2)}</strong> :{' '}
            <strong>{team.expectedPointsAway.toFixed(2)}</strong> over {team.gameCount} games.
          </p>

          {prediction.warnings.length > 0 && (
            <div className="notice" style={{ marginTop: 12 }}>
              {prediction.warnings.join(' ')}
            </div>
          )}
        </>
      )}

      {prediction && (
        <div className="table-scroll" style={{ marginTop: 16 }}>
          <table>
            <thead>
              <tr>
                <th className="left">#</th>
                <th className="left">Type</th>
                <th className="left">{homeTeam.name}</th>
                <th style={{ width: 130 }}>Win chance</th>
                <th className="left">{awayTeam.name}</th>
              </tr>
            </thead>
            <tbody>
              {prediction.slots.map((slot) => (
                <tr key={slot.index}>
                  <td className="left muted">{slot.index + 1}</td>
                  <td className="left">
                    <span className="tag">{slot.kind === 'EZ' ? 'single' : 'double'}</span>
                  </td>
                  <td className="left">
                    <SlotEditor
                      players={homePlayers}
                      playerIds={home[slot.index]?.playerIds ?? []}
                      onChange={(position, playerId) => setSlotPlayer('home', slot.index, position, playerId)}
                    />
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div className="split" style={{ flex: 1 }}>
                        <span style={{ width: `${slot.homeWinProbability * 100}%` }} />
                      </div>
                      <span className="num" style={{ minWidth: 52 }}>
                        {pct(slot.homeWinProbability, 0)}
                      </span>
                    </div>
                  </td>
                  <td className="left">
                    <SlotEditor
                      players={awayPlayers}
                      playerIds={away[slot.index]?.playerIds ?? []}
                      onChange={(position, playerId) => setSlotPlayer('away', slot.index, position, playerId)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="row" style={{ marginTop: 14 }}>
        <button
          className="btn primary"
          onClick={() => void recommendLineup()}
          disabled={optimising || loading || away.length === 0}
          title={`Search every allocation of ${homeTeam.name}'s squad against the away lineup shown`}
        >
          {optimising ? 'Searching…' : `Recommend optimal lineup for ${homeTeam.name}`}
        </button>
        <label className="check" title="How many of the four may be replaced for the second singles block">
          <span className="hint">switchouts</span>
          <select
            value={maxSwitchouts}
            onChange={(e) => setMaxSwitchouts(Number(e.target.value))}
            disabled={optimising}
          >
            <option value={0}>0</option>
            <option value={1}>1</option>
            <option value={2}>2</option>
          </select>
        </label>
        <button className="btn" onClick={() => void loadSuggestions()} disabled={loading}>
          Reset to suggested lineups
        </button>
        <span className="hint">
          {home.length > 0 && sameLineup(home, away) ? 'Both lineups are identical.' : ''}
        </span>
      </div>

      {optimised && (
        <div className="notice ok" style={{ marginTop: 12 }}>
          Best of {optimised.candidatesEvaluated.toLocaleString('de-DE')} allocations, using{' '}
          <strong>{optimised.playersUsed} players</strong>:{' '}
          {optimised.corePlayers.map((p) => p.name).join(', ')}
          {optimised.switchIns.length > 0 && (
            <>
              {' '}
              — switching in <strong>{optimised.switchIns.map((p) => p.name).join(', ')}</strong> for{' '}
              {optimised.switchOuts.map((p) => p.name).join(', ')} in the second singles block
            </>
          )}
          .{' '}
          {optimised.withoutSwitchouts && (
            <>
              Fielding the same four all night gives{' '}
              {pct(optimised.withoutSwitchouts.homeWinProbability, 1)}, so the switchouts are worth{' '}
              <strong>{(optimised.gain * 100).toFixed(2)} pp</strong>.
            </>
          )}
        </div>
      )}
    </section>
  );
}

function SlotEditor({
  players,
  playerIds,
  onChange,
}: {
  players: RatedPlayer[];
  playerIds: number[];
  onChange: (position: number, playerId: number) => void;
}) {
  return (
    <div className="slot-players">
      {playerIds.map((playerId, position) => (
        <select
          key={position}
          value={playerId}
          onChange={(e) => onChange(position, Number(e.target.value))}
          aria-label={`Player ${position + 1}`}
        >
          {players.map((p) => (
            <option key={p.playerId} value={p.playerId}>
              {p.displayName}
            </option>
          ))}
        </select>
      ))}
    </div>
  );
}
